import PropTypes from 'prop-types'
import { ChangeEvent, ReactNode, useEffect, useState } from 'react'
import styled from 'styled-components'

import LoadingSpinner from '@/components/LoadingSpinner/LoadingSpinner'

type ImagePreviewProps = {
  className?: string
  title?: string
  style?: object
  src?: string
  alt?: string
  onError?: () => void
  fallbackImage?: ReactNode
  useLoadingImage?: boolean
}

type FallbackImageProps = {
  className?: string
  style?: object
}

const StyledFallbackImage: any = styled.img`
  color: var(--wg-gray-border);
`

const FloatLoading = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.92);

  ${LoadingSpinner} {
    position: absolute;
    top: calc(50% - 15px);
    left: calc(50% - 15px);
  }
`

const Wrap = styled.span`
  position: relative;
  display: inline-block;
  min-width: 40px;
`

const FallbackImage = ({ className, style }: FallbackImageProps) => (
  <svg xmlns="http://www.w3.org/2000/svg" className={className} style={style} viewBox="0 0 24 24" name="Unknown">
    <title>{'Unknown Image'}</title>
    <path
      d="M23,2H1A1,1,0,0,0,0,3V21a1,1,0,0,0,1,1H23a1,1,0,0,0,1-1V3A1,1,0,0,0,23,2ZM22,4V14.3L17.759,9.35A1,1,0,0,0,17.005,9a.879.879,0,0,0-.757.342l-6.3,7.195L6.707,13.293A.988.988,0,0,0,5.955,13a1,1,0,0,0-.723.358L2,17.238V4Z"
      fill="currentColor"
    />
    <circle fill="currentColor" cx="9" cy="8" r="2" />
  </svg>
)

const StyledUnderloadingImage = styled(FallbackImage)``

const ImagePreview = ({
  onError = () => {},
  className,
  src,
  title = '',
  alt = '',
  style,
  fallbackImage: ProvidedFallbackImage,
  useLoadingImage = true
}: ImagePreviewProps) => {
  const [imageError, setImageError] = useState(false)
  const [imageLoading, setImageLoading] = useState(false)
  const [actualSrc, setActualSrc] = useState<string>()

  const imageLoadError = (e: ChangeEvent<any>) => {
    if (!imageError) {
      setImageError(true)
      setImageLoading(false)
      // Also always show it as non-rounded
      e.target.style.borderRadius = 0

      onError()
    }
  }

  useEffect(() => {
    try {
      // eslint-disable-next-line unicorn/error-message
      if (!src) throw new Error()
      // TODO maybe check that the url ends in a valid image extension?
      new URL(src, window.location.href)
      setImageError(false)
      setImageLoading(true)
      setActualSrc(src)
    } catch {
      setImageError(true)
      setImageLoading(false)
      setActualSrc(undefined)
    }
  }, [src])

  const imageLoaded = () => {
    setImageLoading(false)
  }

  return (
    <>
      {imageLoading && useLoadingImage && (
        <Wrap className={className}>
          <>
            <StyledUnderloadingImage style={style} />
            <FloatLoading>
              <LoadingSpinner />
            </FloatLoading>
          </>
        </Wrap>
      )}
      <StyledFallbackImage
        className={className}
        style={{ ...style, display: imageLoading ? 'none' : 'block' }}
        alt={alt}
        title={title}
        src={actualSrc}
        onLoad={imageLoaded}
        onError={imageLoadError}
        as={imageError ? ProvidedFallbackImage || FallbackImage : 'img'}
      />
    </>
  )
}

ImagePreview.displayName = 'ImagePreview'

ImagePreview.propTypes = {
  children: PropTypes.any
}

export default ImagePreview
